import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ExternalLink, RotateCcw, X } from 'lucide-react';
import { toast } from 'sonner';
import { useState } from 'react';
import {
  Detail,
  DetailList,
  EmptyState,
  Page,
  PageBody,
  PageHeader,
  Panel,
  Toolbar,
} from '@/ui/Panel';
import { Callout } from '@/ui/Callout';
import { DataTable, type Column } from '@/ui/DataTable';
import { SplitView } from '@/ui/SplitView';
import { ChipGroup, Pagination } from '@/ui/Filters';
import { Button } from '@/ui/Button';
import { Badge, Code } from '@/ui/Badge';
import { ConfirmDialog, Dialog } from '@/ui/Dialog';
import { ApiError, api, errorMessage } from '@/lib/api/client';
import { useAtLeast } from '@/lib/auth';
import { useShortcutHint } from '@/app/StatusLine';
import { dateTime, humanize, num, qty as fmtQty, smartDate } from '@/lib/format';
import type {
  CountQuestion,
  ReturnReview,
  ReturnReviewListResponse,
} from '@/lib/api/types';
import {
  countQuestionsFromError,
  storeDayLabel,
} from '@/features/buying/CountConfirmation';

/**
 * The return review queue.
 *
 * A register refund can be rung up without anyone deciding whether the goods
 * are fit to go back on the shelf. Those lines land here. **Approve writes an
 * inventory adjustment** — stock moves the moment the button is pressed —
 * while **deny moves no stock at all**. That asymmetry is the entire screen, so
 * the approve confirmation says the restock out loud.
 *
 * Both decisions are manager-gated (`returns.review`) and idempotent on the
 * server. A 409 means somebody else already decided this line; that is
 * information, not a failure, so it refreshes rather than shouting.
 */

const PAGE_SIZE = 50;

type StatusFilter = 'pending' | 'approved' | 'denied' | 'all';

const STATUS_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'denied', label: 'Denied' },
  { value: 'all', label: 'All' },
];

const REVIEW_TONE = {
  pending: 'warn',
  approved: 'positive',
  denied: 'neutral',
} as const;

interface ReturnsSearch {
  id?: number;
  status: StatusFilter;
  page: number;
}

export const Route = createFileRoute('/_app/returns')({
  staticData: { crumb: 'Returns' },
  validateSearch: (s: Record<string, unknown>): ReturnsSearch => ({
    id: Number(s.id) > 0 ? Number(s.id) : undefined,
    status: STATUS_OPTIONS.some((o) => o.value === s.status)
      ? (s.status as StatusFilter)
      : 'pending',
    page: Number(s.page) > 0 ? Number(s.page) : 1,
  }),
  component: ReturnsScreen,
});

function ReturnsScreen() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  useShortcutHint([
    { keys: 'j / k', label: 'Move' },
    { keys: 'Enter', label: 'Inspect' },
  ]);

  function patch(next: Partial<ReturnsSearch>) {
    void navigate({ search: (prev: ReturnsSearch) => ({ ...prev, ...next }) });
  }

  const listParams = {
    status: search.status,
    page: search.page,
    pageSize: PAGE_SIZE,
  };

  const list = useQuery({
    queryKey: ['return-reviews', 'list', listParams],
    queryFn: () =>
      api.get<ReturnReviewListResponse>('/return-reviews', { params: listParams }),
    placeholderData: (prev) => prev,
  });

  const pending = useQuery({
    queryKey: ['return-reviews', 'count', 'pending'],
    queryFn: () =>
      api.get<ReturnReviewListResponse>('/return-reviews', {
        params: { status: 'pending', pageSize: 1 },
      }),
    staleTime: 30_000,
  });

  const rows = list.data?.data;
  const total = list.data?.total ?? 0;
  const selected = rows?.find((r) => r.id === search.id);

  const columns: Array<Column<ReturnReview>> = [
    {
      id: 'createdAt',
      header: 'Raised',
      width: '7rem',
      mono: true,
      cell: (row) => (
        <span title={dateTime(row.createdAt)}>{smartDate(row.createdAt)}</span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      width: '6.5rem',
      cell: (row) => (
        <Badge tone={REVIEW_TONE[row.status] ?? 'neutral'} dot>
          {humanize(row.status)}
        </Badge>
      ),
    },
    {
      id: 'product',
      header: 'Product',
      cell: (row) => (
        <span className="text-ink truncate text-xs">
          {row.productName ?? `Product ${row.productId}`}
        </span>
      ),
    },
    {
      id: 'quantity',
      header: 'Qty',
      width: '4.5rem',
      align: 'right',
      mono: true,
      cell: (row) => fmtQty(row.quantity),
    },
    {
      id: 'register',
      header: 'Register',
      width: '8rem',
      hideBelow: 'lg',
      cell: (row) => (
        <span className="text-ink-muted truncate text-xs">
          {row.registerName ??
            (row.registerId === null ? '—' : `Register ${row.registerId}`)}
        </span>
      ),
    },
    {
      id: 'decidedBy',
      header: 'Decided by',
      width: '9rem',
      hideBelow: 'xl',
      cell: (row) => (
        <span className="text-ink-muted truncate text-xs">
          {row.decidedByName ?? '—'}
        </span>
      ),
    },
  ];

  return (
    <Page>
      <PageHeader
        eyebrow="Selling"
        title="Returns"
        subtitle="Refund lines waiting on a restock decision"
      />

      <Toolbar>
        <ChipGroup<StatusFilter>
          value={search.status}
          onChange={(v) => patch({ status: v, page: 1, id: undefined })}
          options={STATUS_OPTIONS.map((o) =>
            o.value === 'pending'
              ? { ...o, count: pending.data?.total ?? undefined, tone: 'warn' as const }
              : o
          )}
        />
        <span className="text-2xs text-ink-faint ml-auto">{num(total)} in this view</span>
      </Toolbar>

      <PageBody bleed className="min-h-0 overflow-hidden">
        <SplitView
          storageKey="returns"
          onCloseDetail={() => patch({ id: undefined })}
          placeholder={
            <EmptyState
              compact
              icon={<RotateCcw />}
              title="No return selected"
              description="Pick a line to see what was refunded and decide whether the stock goes back."
            />
          }
          detail={
            selected ? <ReturnInspector key={selected.id} review={selected} /> : undefined
          }
          list={
            <DataTable
              rows={rows}
              columns={columns}
              rowKey={(row) => row.id}
              loading={list.isLoading}
              error={list.error}
              onRetry={() => void list.refetch()}
              onRowOpen={(row) => patch({ id: row.id })}
              isRowActive={(row) => row.id === search.id}
              rowTone={(row) => (row.status === 'pending' ? 'warn' : null)}
              empty={{
                icon: <RotateCcw />,
                title:
                  search.status === 'pending'
                    ? 'Nothing waiting on a decision'
                    : 'No returns in this view',
                description:
                  search.status === 'pending'
                    ? 'Every refund line has been reviewed.'
                    : 'Try another status filter.',
              }}
              footer={
                <Pagination
                  className="w-full"
                  page={search.page}
                  pageSize={PAGE_SIZE}
                  total={total}
                  onPageChange={(page) => patch({ page })}
                />
              }
            />
          }
        />
      </PageBody>
    </Page>
  );
}

/* ── Inspector ─────────────────────────────────────────────────────────────── */

function ReturnInspector({ review }: { review: ReturnReview }) {
  const queryClient = useQueryClient();
  const isManager = useAtLeast('manager');
  const [confirming, setConfirming] = useState<'approve' | 'deny' | null>(null);
  /** TASK-0230 (owner rule C45): the item was counted on or after this refund's day. */
  const [countedAfter, setCountedAfter] = useState<CountQuestion | null>(null);

  const quantity = Number(review.quantity);
  const productLabel = review.productName ?? `Product ${review.productId}`;

  const decide = useMutation({
    mutationFn: ({
      action,
      confirmAfterCount = false,
    }: {
      action: 'approve' | 'deny';
      confirmAfterCount?: boolean;
    }) =>
      api.post<unknown>(
        `/return-reviews/${review.id}/${action}`,
        confirmAfterCount ? { confirmAfterCount: true } : undefined
      ),
    onSuccess: (_result, { action }) => {
      toast.success(action === 'approve' ? 'Return approved' : 'Return denied', {
        description:
          action === 'approve'
            ? `${fmtQty(quantity)} × ${productLabel} restocked.`
            : 'No stock was moved.',
      });
      refreshAll();
      setConfirming(null);
      setCountedAfter(null);
    },
    onError: (err) => {
      // TASK-0230: the item was counted on or after the refund. Nothing was written; ask,
      // with "Don't add" as the default.
      const asked = countQuestionsFromError(err);
      if (asked) {
        setConfirming(null);
        setCountedAfter(asked[0] ?? null);
        return;
      }
      // Both routes are idempotent and answer 409 when the other decision
      // already landed. Somebody else got there first — that is the queue
      // working, not a failure, so it reads as information and refreshes.
      if (err instanceof ApiError && err.status === 409) {
        toast.info('Already decided', {
          description: `${errorMessage(err)} — refreshed with the current state.`,
        });
        refreshAll();
      } else {
        toast.error('Could not record that decision', {
          description: errorMessage(err),
        });
      }
      setConfirming(null);
    },
  });

  function refreshAll() {
    // No optimistic update: the server owns the decision and the stock it moved.
    void queryClient.invalidateQueries({ queryKey: ['return-reviews'] });
    void queryClient.invalidateQueries({ queryKey: ['inventory'] });
    void queryClient.invalidateQueries({ queryKey: ['badges'] });
  }

  return (
    <div className="space-y-3 p-3">
      <div>
        <Badge tone={REVIEW_TONE[review.status] ?? 'neutral'} dot>
          {humanize(review.status)}
        </Badge>
        <div className="text-ink mt-1.5 text-sm font-medium">{productLabel}</div>
        <div className="text-ink font-mono text-xl tabular-nums">
          {fmtQty(quantity)}
          <span className="text-ink-faint ml-1 text-xs">units</span>
        </div>
      </div>

      {review.status === 'pending' ? (
        <Callout tone="warn" title="Stock has not moved yet">
          The refund was already given at the register. Approving is what puts the
          {` ${fmtQty(quantity)} `}
          back on hand; denying leaves on-hand exactly as it is.
        </Callout>
      ) : null}

      <Panel title="Origin">
        <DetailList>
          <Detail label="Receipt">
            <Link
              to="/transactions/$id"
              params={{ id: review.transactionId }}
              className="text-signal-ink flex items-center gap-1 font-mono hover:underline"
            >
              {review.transactionId.slice(0, 8).toUpperCase()}
              <ExternalLink className="size-3" />
            </Link>
          </Detail>
          <Detail label="Sold at">
            {review.transactionOccurredAt ? dateTime(review.transactionOccurredAt) : '—'}
          </Detail>
          <Detail label="Register">
            {review.registerName ??
              (review.registerId === null ? '—' : `Register ${review.registerId}`)}
          </Detail>
          <Detail label="Raised">{dateTime(review.createdAt)}</Detail>
          <Detail label="Line" mono>
            <Code>{review.transactionItemId}</Code>
          </Detail>
          {review.notes ? <Detail label="Notes">{review.notes}</Detail> : null}
        </DetailList>
      </Panel>

      {review.status !== 'pending' ? (
        <Panel title="Decision">
          <DetailList>
            <Detail label="Outcome">{humanize(review.status)}</Detail>
            <Detail label="By">{review.decidedByName ?? '—'}</Detail>
            <Detail label="When">
              {review.decidedAt ? dateTime(review.decidedAt) : '—'}
            </Detail>
          </DetailList>
        </Panel>
      ) : null}

      {review.status === 'pending' && isManager ? (
        <div className="flex gap-2">
          <Button
            variant="primary"
            size="sm"
            className="flex-1 justify-center"
            icon={<Check className="size-3.5" />}
            onClick={() => setConfirming('approve')}
          >
            Approve &amp; restock
          </Button>
          <Button
            variant="danger"
            size="sm"
            className="flex-1 justify-center"
            icon={<X className="size-3.5" />}
            onClick={() => setConfirming('deny')}
          >
            Deny
          </Button>
        </div>
      ) : null}

      {review.status === 'pending' && !isManager ? (
        <Callout tone="info" title="A manager has to decide this">
          Approving or denying a return review needs the `returns.review` capability,
          which starts at manager.
        </Callout>
      ) : null}

      <ConfirmDialog
        open={confirming === 'approve'}
        onOpenChange={(next) => setConfirming(next ? 'approve' : null)}
        title="Approve and restock"
        description="This is the stock-in event for the refund."
        confirmLabel="Approve and restock"
        variant="primary"
        loading={decide.isPending}
        consequences={[
          `Writes a +${fmtQty(quantity)} return adjustment against ${productLabel}.`,
          'On-hand goes up immediately — make sure the goods are physically back.',
          'Recorded against you in the inventory audit trail.',
        ]}
        onConfirm={() => decide.mutate({ action: 'approve' })}
      />

      <Dialog
        open={countedAfter !== null}
        onOpenChange={(next) => {
          if (!next) setCountedAfter(null);
        }}
        title={
          countedAfter?.sameDayAsCount
            ? 'Counted the same day as this refund'
            : 'Counted after this refund'
        }
        width="sm"
        footer={
          <>
            <Button
              variant="default"
              loading={decide.isPending && decide.variables?.action === 'approve'}
              onClick={() =>
                decide.mutate({ action: 'approve', confirmAfterCount: true })
              }
            >
              Add to stock
            </Button>
            <Button
              variant="primary"
              autoFocus
              loading={decide.isPending && decide.variables?.action === 'deny'}
              onClick={() => decide.mutate({ action: 'deny' })}
            >
              Don&apos;t add (deny)
            </Button>
          </>
        }
      >
        {countedAfter ? (
          <p className="text-ink text-sm leading-relaxed">
            {/* TASK-0230 review F7: a count on the refund's own day also asks (on or after). */}
            {countedAfter.sameDayAsCount
              ? `This item was counted on ${storeDayLabel(countedAfter.lastCountedOn)}, the same day as this refund. If it was back on the shelf when it was counted, the count already includes it. Add it to stock anyway?`
              : `This item was counted on ${storeDayLabel(countedAfter.lastCountedOn)}, after this refund on ${storeDayLabel(countedAfter.receiptDate)}. The count already includes whatever was on the shelf. Add it to stock anyway?`}
          </p>
        ) : null}
      </Dialog>

      <ConfirmDialog
        open={confirming === 'deny'}
        onOpenChange={(next) => setConfirming(next ? 'deny' : null)}
        title="Deny this return"
        description="Use this when the goods are not fit to sell again."
        confirmLabel="Deny"
        loading={decide.isPending}
        consequences={[
          'Moves no stock at all — on-hand is unchanged.',
          'Closes the line so it stops showing in the pending queue.',
          'The refund itself is untouched; the customer keeps their money.',
        ]}
        onConfirm={() => decide.mutate({ action: 'deny' })}
      />
    </div>
  );
}
